import { state } from '../state';
import { showToast } from '../components/toast';

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function avatarBg(personId: string): string {
  let hash = 0;
  for (let i = 0; i < personId.length; i++) hash = ((hash << 5) - hash + personId.charCodeAt(i)) | 0;
  const idx = (Math.abs(hash) % 12) + 1;
  return `var(--avatar-c${idx})`;
}

// Restrict role color to #rgb / #rrggbb before inlining into CSS custom
// property so a malicious/corrupt stored color can't break out of the
// style attribute. `esc()` uses textContent which does NOT escape quotes.
function safeHex(s: string): string {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s) ? s : '';
}

/** Open the user profile card for a person, anchored near the given element if
 *  provided, otherwise centred. Single source of truth — every member-list,
 *  mention, author-name, and DM list click should call this. */
export function openProfileCard(personId: string, anchor?: HTMLElement | { x: number; y: number }): void {
  document.querySelectorAll('.profile-popup, .profile-card-modal-backdrop').forEach(el => el.remove());

  const person = state.people.find(p => p.id === personId);
  if (!person) {
    showToast('Profile not found', 'err');
    return;
  }

  const initial = (person.name || '?').charAt(0).toUpperCase();
  const status = (person as { status?: string }).status === 'active' ? 'Online' : 'Offline';
  const myId = state.ownerId || '';
  const isSelf = personId === myId;

  // Discord parity: viewer is an admin/owner of the current server → render
  // a "+" affordance next to the role chip so they can attach a new role
  // without opening the full server settings. Backend hooks it to
  // POST /api/people/:id/roles via the existing roles handler.
  const sidForPerm = state.currentServer;
  const viewerMembership = sidForPerm
    ? state.serverMembers.find(m => m.server_id === sidForPerm && m.person_id === myId)
    : null;
  const viewerCanAssignRoles = viewerMembership?.role === 'owner' || viewerMembership?.role === 'admin';

  // Discord parity: "Member Since" line below the role chip. Pulled from the
  // server_members.joined_at row so it's per-server (matches Discord), with a
  // graceful fallback to person.created_at when the membership row is missing.
  const memberRow = sidForPerm
    ? state.serverMembers.find(m => m.server_id === sidForPerm && m.person_id === personId)
    : null;
  const memberSinceIso = memberRow?.joined_at || person.created_at || '';
  const memberSinceLabel = memberSinceIso
    ? new Date(memberSinceIso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  // Derive the highest-position role for this person in the current server
  // so the card can show it under the name (Discord parity).
  const sid = state.currentServer;
  const topRole = sid
    ? state.roles.find(r => r.server_id === sid && !!state.roleColors[personId] && state.roleColors[personId] === r.color)
    : null;
  const roleName = topRole?.name || '';
  const roleColor = safeHex(state.roleColors[personId] || '');

  // Persisted server-mute toggle lives in localStorage; it's a client-side
  // flag the voice layer reads when rendering other participants. Real
  // propagation to LiveKit is deferred.
  const muteKey = `hub-people-server-mute-${personId}`;
  const serverMuted = localStorage.getItem(muteKey) === '1';

  // Discord-parity: per-viewer private note about this user. Only the viewer
  // sees it. Persisted client-side only \u2014 no backend round-trip needed.
  const noteKey = `hub-people-note-${personId}`;
  const userNote = localStorage.getItem(noteKey) || '';

  // Block list: drop messages and DMs from blocked users. Backed by a Set
  // in localStorage so the chat renderer can filter without a per-row read.
  const blockKey = 'hub-people-blocked';
  const blockedSet = new Set<string>(JSON.parse(localStorage.getItem(blockKey) || '[]') as string[]);
  const isBlocked = blockedSet.has(personId);

  const popup = document.createElement('div');
  popup.className = 'profile-popup';
  // Drive banner + role tinting via a single custom property so markup
  // below stays clean and safeHex above guards the injected value.
  if (roleColor) popup.style.setProperty('--role-color', roleColor);

  const statusOnline = status === 'Online';
  popup.innerHTML = `
    <div class="profile-popup-banner"></div>
    <div class="profile-popup-avatar profile-popup-avatar-lg" style="background:${avatarBg(personId)}">${esc(initial)}</div>
    <div class="profile-popup-name${roleColor ? ' profile-popup-name-colored' : ''}">${esc(person.name)}</div>
    ${roleName ? `<div class="profile-popup-role${roleColor ? ' profile-popup-role-colored' : ''}">
      <span class="profile-popup-role-dot"></span>${esc(roleName)}
      ${viewerCanAssignRoles && !isSelf ? '<button class="profile-popup-role-add" data-action="role-add" type="button" title="Add a role" aria-label="Add a role">+</button>' : ''}
    </div>` : (viewerCanAssignRoles && !isSelf ? '<div class="profile-popup-role"><button class="profile-popup-role-add" data-action="role-add" type="button" title="Add a role" aria-label="Add a role">+ Add role</button></div>' : '')}
    <div class="profile-popup-status"><span class="profile-popup-status-dot${statusOnline ? ' profile-popup-status-dot-online' : ''}"></span>${esc(status)}</div>
    ${person.email ? `<div class="profile-popup-detail">${esc(person.email)}</div>` : ''}
    ${person.phone ? `<div class="profile-popup-detail">${esc(person.phone)}</div>` : ''}
    ${memberSinceLabel ? `<div class="profile-popup-section-title">Member Since</div><div class="profile-popup-detail">${esc(memberSinceLabel)}</div>` : ''}
    <div class="profile-popup-section-title">About me</div>
    <div class="profile-popup-detail profile-popup-bio">${person.notes ? esc(person.notes) : '<em class="profile-popup-dim">Nothing here yet.</em>'}</div>
    ${isSelf ? '' : `
      <div class="profile-popup-mutual">
        <div class="profile-popup-mutual-tabs" role="tablist">
          <button class="profile-popup-mutual-tab active" data-mutual-tab="servers" role="tab">Mutual Servers</button>
          <button class="profile-popup-mutual-tab" data-mutual-tab="friends" role="tab">Mutual Friends</button>
        </div>
        <div class="profile-popup-mutual-body" data-mutual-body><div class="profile-popup-dim" style="padding:6px 0">Loading…</div></div>
      </div>
    `}
    ${isSelf ? '' : `
      <div class="profile-popup-section-title">Note</div>
      <textarea class="profile-popup-note" data-action="note" placeholder="Click to add a note (only you can see it)" maxlength="500" rows="2">${esc(userNote)}</textarea>
      <div class="profile-popup-section-title">Server controls</div>
      <label class="profile-popup-toggle">
        <input type="checkbox" data-action="toggle-mute"${serverMuted ? ' checked' : ''}>
        <span>Server mute</span>
      </label>
      <label class="profile-popup-toggle">
        <input type="checkbox" data-action="toggle-block"${isBlocked ? ' checked' : ''}>
        <span>Block messages from this user</span>
      </label>
      <button class="profile-popup-link" data-action="voice-settings">Voice settings\u2026</button>
    `}
    <div class="profile-popup-actions">
      ${isSelf
        ? '<button class="btn btn-sm btn-secondary profile-popup-action-btn" data-action="copy-id">Copy User ID</button>'
        : '<button class="btn btn-sm btn-primary profile-popup-action-btn" data-action="dm">Send Message</button>' +
          '<button class="btn btn-sm btn-secondary profile-popup-action-btn" data-action="copy-id">Copy User ID</button>'}
    </div>
  `;

  document.body.appendChild(popup);

  // Position: prefer a fixed anchor, else viewport-clamp near the anchor's
  // bounding rect, else centre. Body-appended so #channel-list overflow:hidden
  // can never clip it again (the bug Mitchell screenshotted).
  // Measure after append so tall variants (with note + toggles + voice btn)
  // don't clip off-screen bottom — prior hardcoded 220px undersized those.
  const measured = popup.getBoundingClientRect();
  const popupRect = { width: measured.width || 280, height: measured.height || 320 };
  const isMobile = window.innerWidth <= 540;
  if (isMobile) {
    popup.classList.add('profile-popup-mobile');
  } else if (anchor instanceof HTMLElement) {
    const r = anchor.getBoundingClientRect();
    let left = r.right + 8;
    let top = r.top;
    if (left + popupRect.width > window.innerWidth - 12) left = Math.max(12, r.left - popupRect.width - 8);
    if (top + popupRect.height > window.innerHeight - 12) top = Math.max(12, window.innerHeight - popupRect.height - 12);
    popup.style.position = 'fixed';
    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
  } else if (anchor && typeof anchor === 'object' && 'x' in anchor) {
    let left = anchor.x;
    let top = anchor.y;
    if (left + popupRect.width > window.innerWidth - 12) left = window.innerWidth - popupRect.width - 12;
    if (top + popupRect.height > window.innerHeight - 12) top = window.innerHeight - popupRect.height - 12;
    popup.style.position = 'fixed';
    popup.style.left = `${Math.max(12, left)}px`;
    popup.style.top = `${Math.max(12, top)}px`;
  } else {
    popup.classList.add('profile-popup-centered');
  }

  popup.querySelector<HTMLButtonElement>('[data-action="dm"]')?.addEventListener('click', () => {
    popup.remove();
    window.dispatchEvent(new CustomEvent('hub-open-dm', { detail: { personId } }));
  });
  popup.querySelector<HTMLButtonElement>('[data-action="copy-id"]')?.addEventListener('click', () => {
    void navigator.clipboard.writeText(personId).then(() => showToast('User ID copied', 'ok'));
    popup.remove();
  });
  popup.querySelector<HTMLInputElement>('[data-action="toggle-mute"]')?.addEventListener('change', (e) => {
    const checked = (e.currentTarget as HTMLInputElement).checked;
    if (checked) localStorage.setItem(muteKey, '1');
    else localStorage.removeItem(muteKey);
    showToast(checked ? 'Server-muted locally' : 'Server-mute cleared', 'ok');
  });
  popup.querySelector<HTMLInputElement>('[data-action="toggle-block"]')?.addEventListener('change', (e) => {
    const checked = (e.currentTarget as HTMLInputElement).checked;
    const cur = new Set<string>(JSON.parse(localStorage.getItem(blockKey) || '[]') as string[]);
    if (checked) cur.add(personId); else cur.delete(personId);
    localStorage.setItem(blockKey, JSON.stringify([...cur]));
    showToast(checked ? 'User blocked' : 'User unblocked', 'ok');
    // Hide existing messages from blocked user immediately so the user
    // sees the effect without waiting for next render.
    document.querySelectorAll<HTMLElement>(`.msg[data-author="${personId}"]`).forEach(el => {
      el.style.display = checked ? 'none' : '';
    });
  });
  // Note: persist on blur OR after 800ms idle to avoid every-keystroke writes.
  const noteEl = popup.querySelector<HTMLTextAreaElement>('[data-action="note"]');
  if (noteEl) {
    let noteTimer: ReturnType<typeof setTimeout> | null = null;
    const saveNote = (): void => {
      const v = noteEl.value.trim();
      if (v) localStorage.setItem(noteKey, v);
      else localStorage.removeItem(noteKey);
    };
    noteEl.addEventListener('input', () => {
      if (noteTimer) clearTimeout(noteTimer);
      noteTimer = setTimeout(saveNote, 800);
    });
    noteEl.addEventListener('blur', saveNote);
  }
  popup.querySelector<HTMLButtonElement>('[data-action="voice-settings"]')?.addEventListener('click', () => {
    popup.remove();
    showToast('Voice settings — coming soon', 'ok');
  });

  // Role-add "+" — opens a tiny menu listing assignable roles. Falls back to
  // a toast when the backend rejects (dev2 may not have wired the endpoint).
  popup.querySelector<HTMLButtonElement>('[data-action="role-add"]')?.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (!sidForPerm) return;
    const candidates = state.roles.filter(r => r.server_id === sidForPerm);
    if (candidates.length === 0) { showToast('No roles defined yet', 'warn'); return; }
    const items = candidates.map(r => ({ label: r.name, action: () => {
      void import('../api').then(({ post }) =>
        post(`/api/people/${encodeURIComponent(personId)}/roles`, { server_id: sidForPerm, role_id: r.id })
          .then(() => showToast(`Assigned role ${r.name}`, 'ok'))
          .catch(() => showToast('Role assignment endpoint not yet available', 'warn'))
      );
    }}));
    void import('./context-menu').then(({ showContextMenu }) => {
      const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
      showContextMenu(r.left, r.bottom + 4, items);
    });
  });

  // Mutual Servers / Mutual Friends — fetch lazily on first tab click, cache
  // for the lifetime of this popup. Backend endpoint may not be live yet;
  // empty state explains the situation rather than failing loudly.
  type MutualPayload = { servers?: Array<{ id: string; name: string }>; friends?: Array<{ id: string; name: string }> };
  let mutualCache: MutualPayload | null = null;
  const mutualBody = popup.querySelector<HTMLElement>('[data-mutual-body]');

  const renderMutualTab = async (which: 'servers' | 'friends'): Promise<void> => {
    if (!mutualBody) return;
    popup.querySelectorAll<HTMLButtonElement>('.profile-popup-mutual-tab').forEach(t => {
      t.classList.toggle('active', t.dataset['mutualTab'] === which);
    });
    mutualBody.innerHTML = '<div class="profile-popup-dim" style="padding:6px 0">Loading…</div>';
    try {
      if (!mutualCache) {
        const { get: fetchGet } = await import('../api');
        mutualCache = await fetchGet<MutualPayload>(
          `/api/people/${encodeURIComponent(personId)}/mutual?viewer=${encodeURIComponent(myId)}`,
        );
      }
      const list = which === 'servers' ? (mutualCache.servers ?? []) : (mutualCache.friends ?? []);
      if (list.length === 0) {
        mutualBody.innerHTML = `<div class="profile-popup-dim" style="padding:6px 0">No mutual ${which} found.</div>`;
        return;
      }
      mutualBody.innerHTML = list.map(item =>
        `<div class="profile-popup-mutual-row">${esc(item.name)}</div>`
      ).join('');
    } catch {
      mutualBody.innerHTML = '<div class="profile-popup-dim" style="padding:6px 0">Mutual endpoint not yet available.</div>';
    }
  };

  popup.querySelectorAll<HTMLButtonElement>('.profile-popup-mutual-tab').forEach(t => {
    t.addEventListener('click', () => {
      const which = (t.dataset['mutualTab'] as 'servers' | 'friends') || 'servers';
      void renderMutualTab(which);
    });
  });
  if (!isSelf) void renderMutualTab('servers');

  const close = (e: MouseEvent) => {
    if (!popup.contains(e.target as Node)) {
      popup.remove();
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', escClose);
    }
  };
  const escClose = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { popup.remove(); document.removeEventListener('click', close); document.removeEventListener('keydown', escClose); }
  };
  setTimeout(() => {
    document.addEventListener('click', close);
    document.addEventListener('keydown', escClose);
  }, 0);
}
